import { useState, useEffect, useContext } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useAuth from 'providers/Auth/useAuth';

import { getCooperative } from 'services/API/Cooperative';
import {
  getCulturesByExploitationId,
  getExploitationInformations,
  getGroundTypeByExploitationId,
  getParcelsByExploitation,
} from 'services/API/Exploitation';

import { setInitialStateSwitchIsOrganic } from 'utils/tools_functions';

import ExploitationHome from 'components/pages/Exploitation/Home';
import Parcels from './Parcels';

import { sendEvent } from 'utils/Event';

import 'assets/styles/modal.style.scss';
import 'assets/styles/section.style.scss';
import CooperativeImport from '../Cooperative/Import';
import CooperativeExploitation from '../Cooperative/Exploitation';
import Result from '../Cooperative/Result';
import { ArableContext, ExploitationContext, LoadingContext } from 'utils/context';
import { useTranslation } from 'react-i18next';
import CulturesDataGridExploitation from './CulturesDataGridExploitation';
import { getRandomColor, emptyCulture } from 'utils/culturesColors';
import GeofoliaProcess from '../Cooperative/GeofoliaProcess';
import RotateEffectsDataGridExploitation from './RotateEffectsDataGridExploitation';
import GroundTypeEffectsDataGridExploitation from './GroundTypeEffectsDataGridExploitation';
import ProcedureChangeCampaign from './ProcedureCampaign';
import ProcedureImport from './ProcedureImport';
import CultureGroup from '../Cooperative/CultureGroup/CultureGroup';
import Simulation from './Simulation/Simulation';
import { default as ResultSimulation } from './Simulation/Result/Result';
import PageLayout from './PageLayout';
import SimulationIndex from './Simulation';

const Exploitation = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const cooperativeId = localStorage.getItem('cooperative');
  const idGet = cooperativeId ? cooperativeId : user.cooperative.id;
  const { setFullScreen } = useContext(LoadingContext);
  const [arableSurface, setArableSurface] = useState(0);
  const value = {
    arableSurface,
    setArableSurface,
  };
  const { setForceLoader } = useContext(LoadingContext);

  const { exploitationContext, setExploitationContext, setCulturesColors } =
    useContext(ExploitationContext);
  const [nitrogenCover, setNitrogenCover] = useState(null);
  const [nitrogenSupplyByCoverOrganic, setNitrogenSupplyByCoverOrganic] = useState(null);
  const [biomassSupplyByCover, setBiomassSupplyByCover] = useState(null);
  const [cultures, setCultures] = useState([]);
  const [groundTypes, setGroundTypes] = useState([]);
  const [cooperative, setCooperative] = useState([]);

  const [exploitationId, setExploitationId] = useState(localStorage.getItem('exploitation'));

  const loadData = async () => {
    setForceLoader(true);
    if (!localStorage.getItem('exploitation')) {
      setExploitationId(localStorage.getItem('exploitation'));
      setFullScreen(true);
    }
    try {
      const response = await getCooperative(idGet);
      setCooperative(response?.data.data);
      setInitialStateSwitchIsOrganic(response?.data?.data?.hasConventionalData);
    } catch (err) {
      toast.error(err.data);
    } finally {
      setForceLoader(false);
    }
  };

  const setColorContext = (data) => {
    let getStructureColors = [];
    let userCulturesColors = data.map((c) => c.color).filter((c) => c && c !== null);
    data.forEach((culture) => {
      let randomColor = getRandomColor(userCulturesColors);
      userCulturesColors.push('#' + randomColor);
      getStructureColors.push({
        id: culture.id,
        name: culture.name,
        color: culture.color ? culture.color.replace('#', '') : randomColor,
        isArable: culture.isArable,
        irrigationWaterNeeded: culture.irrigationWaterNeeded,
      });
    });
    getStructureColors.push(emptyCulture);
    setCulturesColors(getStructureColors);
  };

  const loadDataExploitation = async (
    isOrganic,
    exploitId,
    targetCampaign = false,
    sort = 'name',
    hidden = false,
  ) => {
    if (!exploitId) {
      exploitId = localStorage.getItem('exploitation');
    }
    try {
      const results = await Promise.all([
        getExploitationInformations(exploitId),
        getCulturesByExploitationId(exploitId, isOrganic, sort, hidden),
        getParcelsByExploitation(exploitId, targetCampaign),
        getGroundTypeByExploitationId(exploitId),
      ]);
      const groundTypes =
        [
          ...new Map(
            results[1].data.data
              ?.flatMap((culture) => culture.groundTypeEffects || [])
              ?.map((effect) => [
                effect.groundType.id,
                { id: effect.groundType.id, name: effect.groundType.name },
              ]),
          ).values(),
        ] || [];
      setExploitationContext({
        ...results[0].data.data,
        cultures:
          sort !== 'rotation'
            ? results[1].data.data.sort((a, b) => a.name.localeCompare(b.name))
            : results[1].data.data,
        parcels: results[2].data.data,
        groundTypes: results[3].data.data,
      });
      setArableSurface(results[0].data.data?.arableTotalSurface);
      setNitrogenCover({ nitrogenSupplyByCover: results[0].data.data?.nitrogenSupplyByCover });
      setNitrogenSupplyByCoverOrganic({
        nitrogenSupplyByCoverOrganic: results[0].data.data?.nitrogenSupplyByCoverOrganic,
      });
      setBiomassSupplyByCover({
        biomassSupplyByCover: results[0].data.data?.biomassSupplyByCover,
      });
      setCultures(results[1].data);

      setGroundTypes(groundTypes);

      if (results[1].data.data) {
        setColorContext(results[1].data.data);
      }

      return results[0];
    } catch (error) {
      toast.error(error.data);
    }
  };

  useEffect(() => {
    user.cooperative && loadData();
  }, [user]);

  // Life Hook Dismount
  useEffect(() => {
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  return (
    <div className="ExploitationContainer">
      <ArableContext.Provider value={value}>
        <Routes>
          <Route
            path="/"
            element={
              <PageLayout>
                <ExploitationHome
                  setExploitation={loadDataExploitation}
                  exploitation={exploitationContext}
                  cooperativeId={idGet}
                />
              </PageLayout>
            }
          />
          <Route
            path="parcels"
            element={
              <PageLayout>
                <Parcels
                  exploitation={exploitationContext}
                  setExploitation={loadDataExploitation}
                />
              </PageLayout>
            }
          />
          <Route
            path="cultures"
            element={
              <PageLayout title={t('title.culturesList')}>
                <CulturesDataGridExploitation
                  exploitation={exploitationContext}
                  setExploitation={loadDataExploitation}
                  structureId={idGet}
                  setColorContext={setColorContext}
                  user={user}
                  nitrogenCover={nitrogenCover}
                  setNitrogenCover={setNitrogenCover}
                  nitrogenSupplyByCoverOrganic={nitrogenSupplyByCoverOrganic}
                  setNitrogenSupplyByCoverOrganic={setNitrogenSupplyByCoverOrganic}
                  biomassSupplyByCover={biomassSupplyByCover}
                  setBiomassSupplyByCover={setBiomassSupplyByCover}
                  cultures={cultures}
                />
              </PageLayout>
            }
          />
          <Route
            path="groundTypesEffects"
            element={
              <PageLayout title={t('title.groundTypeMatrix')}>
                <GroundTypeEffectsDataGridExploitation
                  exploitation={exploitationContext}
                  setExploitation={loadDataExploitation}
                  from={'exploitations'}
                  cultures={cultures}
                  groundTypes={groundTypes}
                />
              </PageLayout>
            }
          />
          <Route
            path="cultureGroup"
            element={
              <PageLayout title={t('title.nonReturnGroupsList')}>
                <CultureGroup
                  ownerId={exploitationContext?.id}
                  from={'exploitations'}
                  setExploitation={loadDataExploitation}
                />
              </PageLayout>
            }
          />
          <Route
            path="rotateEffects"
            element={
              <PageLayout title={t('title.rotationMatrix')}>
                <RotateEffectsDataGridExploitation
                  exploitation={exploitationContext}
                  setExploitation={loadDataExploitation}
                  from={'exploitations'}
                  cultures={cultures}
                />
              </PageLayout>
            }
          />
          <Route
            path="simulation"
            element={
              <SimulationIndex
                exploitationSelected={exploitationContext}
                setExploitation={loadDataExploitation}
              />
            }
          />
          <Route
            path="result"
            element={
              <PageLayout>
                <ResultSimulation
                  exploitationSelected={exploitationContext}
                  setExploitation={loadDataExploitation}
                  culturesFromParent={cultures?.data?.filter(
                    (culture) => culture.isCustomized === true,
                  )}
                />
              </PageLayout>
            }
          />
          <Route
            path="import"
            element={
              <PageLayout>
                <CooperativeImport />
              </PageLayout>
            }
          />
          <Route
            path="import/exploitation"
            element={
              <PageLayout>
                <CooperativeExploitation groundTypes={cooperative?.groundTypes} />
              </PageLayout>
            }
          />
          <Route
            path="import/result"
            element={
              <PageLayout>
                <Result />
              </PageLayout>
            }
          />
          <Route
            exact
            path="import/geofolia/process"
            element={
              <PageLayout>
                <GeofoliaProcess />
              </PageLayout>
            }
          />
          <Route
            exact
            path="import/procedure"
            element={
              <PageLayout>
                <ProcedureImport
                  exploitationSelected={exploitationContext}
                  loadDataExploitation={loadDataExploitation}
                  forecastCampaign={exploitationContext?.forecastCampaign}
                />
              </PageLayout>
            }
          />
          <Route
            path="campaign"
            element={
              <PageLayout>
                <ProcedureChangeCampaign
                  exploitationSelected={exploitationContext}
                  loadDataExploitation={loadDataExploitation}
                />
              </PageLayout>
            }
          />
          <Route
            path="simupoc"
            element={
              <PageLayout>
                <Simulation
                  exploitationSelected={exploitationContext}
                  loadDataExploitation={loadDataExploitation}
                />
              </PageLayout>
            }
          />
          <Route element={<Navigate to="/" />} />
        </Routes>
      </ArableContext.Provider>
    </div>
  );
};

export default Exploitation;
